import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Button,
  IconButton,
  Chip,
  Card,
  Typography,
  TextField,
  MenuItem,
  Stack,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { Icon } from '@iconify/react';
import { useQueryClient } from '@tanstack/react-query';
import intl from 'components/i18n/ReactIntlWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { SCOPES } from 'common/permissions';
import { ENTITY_TYPE } from 'common/constants';
import { useDialog } from 'common/DialogProvider';
import AccountFormDrawer from './AccountFormDrawer';
import { 
  useAccountList, 
  useDeleteAccount,
  useAccountTypes,
  useAccountCategories,
  AccountTypeData,
  AccountCategoryData
} from 'store/registry/useAccountManagement';
import { useAccountBalances } from 'store/registry/useAccountBalance';
import { 
  Account, 
  AccountFilters
} from 'types/account';

const AccountList = () => {
  const navigate = useNavigate();
  const toast = useToastMessageQueue();
  const queryClient = useQueryClient();
  const { openDialog } = useDialog();
  const { data: accountTypes = [] } = useAccountTypes();
  const { data: accountCategories = [] } = useAccountCategories();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState<AccountFilters>({
    search: '',
    isActive: true,
  });
  
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Initialize expanded categories when account categories are loaded
  useEffect(() => {
    if (accountCategories.length > 0) {
      const allCategoryKeys = accountCategories.map(cat => cat.labelKey);
      setExpandedCategories(new Set(allCategoryKeys));
    }
  }, [accountCategories]);

  // Fetch accounts with filters
  const { data: accountsData, isLoading } = useAccountList({
    page: 1,
    pageSize: 500,
    filters,
  });

  // Extract account IDs for balance fetching
  const accountIds = useMemo(() => {
    return accountsData?.data.map(acc => acc.id) || [];
  }, [accountsData]);

  // Fetch balances for all accounts from Ledger API
  const { data: balances, isLoading: balancesLoading } = useAccountBalances(accountIds, true);

  // Create balance map for quick lookup
  const balanceMap = useMemo(() => {
    const map = new Map<number, number>();
    balances?.forEach(balance => {
      map.set(balance.accountId, balance.balance);
    });
    return map;
  }, [balances]);

  // Delete mutation
  const deleteMutation = useDeleteAccount();

  // Group accounts by category using database-driven categories
  const groupedAccounts = useMemo(() => {
    const groups = new Map<string, Account[]>();
    
    accountsData?.data.forEach(account => {
      // Find account type to determine category
      const accountType = accountTypes.find(at => at.id === account.accountTypeId);
      
      // Use category from database or fallback to 'Other'
      const categoryKey = accountType?.categoryLabelKey || 'accountCategory.other';
      
      if (!groups.has(categoryKey)) {
        groups.set(categoryKey, []);
      }
      groups.get(categoryKey)!.push(account);
    });

    return groups;
  }, [accountsData, accountTypes]);

  // Calculate category summaries
  const categorySummaries = useMemo(() => {
    const summaries = new Map<string, { count: number; balance: number }>();
    
    groupedAccounts.forEach((accounts, categoryKey) => {
      const totalBalance = accounts.reduce((sum, account) => {
        return sum + (balanceMap.get(account.id) || 0);
      }, 0);
      
      summaries.set(categoryKey, {
        count: accounts.length,
        balance: totalBalance,
      });
    });

    return summaries;
  }, [groupedAccounts, balanceMap]);

  const handleFilterChange = useCallback((key: keyof AccountFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({ isActive: true });
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedAccount(null);
    setReadOnlyMode(false);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((account: Account) => {
    // Use setTimeout to ensure state updates in correct order
    setSelectedAccount(account);
    setReadOnlyMode(false);
    setTimeout(() => {
      setDrawerOpen(true);
    }, 0);
  }, []);

  const handleDetails = useCallback((account: Account) => {
    setSelectedAccount(account);
    setReadOnlyMode(true);
    setDrawerOpen(true);
  }, []);

  const handleSummary = useCallback((account: Account) => {
    navigate(`/account/detail/${account.id}`);
  }, [navigate]);

  const handleDelete = useCallback(
    (id: number, accountName: string) => {
      const dialogId = openDialog({
        title: intl.get('accountManagement.deleteAccountTitle'),
        message: intl.get('accountManagement.deleteAccountMessage').replace('{accountName}', accountName),
        type: 'confirm',
        confirmButton: {
          confirmLabel: intl.get('common.delete'),
          handleConfirm: (message) => {
            deleteMutation.mutate(id);
            message.handleClose(message);
          },
        },
        cancelButton: {
          cancelLabel: intl.get('common.cancel'),
          handleCancel: (message) => {
            message.handleClose(message);
          },
        },
      });
    },
    [openDialog, deleteMutation]
  );

  const handleDrawerSuccess = useCallback(() => {
    setDrawerOpen(false);
    queryClient.invalidateQueries({ queryKey: ['accounts'] });
  }, [queryClient]);

  // Base columns that depend on balanceMap and balancesLoading  
  const baseColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'accountNumber',
        headerName: intl.get('accountManagement.accountNumber'),
        flex: 1,
        minWidth: 130,
      },
      {
        field: 'accountName',
        headerName: intl.get('accountManagement.accountName'),
        flex: 1.5,
        minWidth: 200,
        sortable: true,
      },
      {
        field: 'accountType',
        headerName: intl.get('accountManagement.type'),
        flex: 1.2,
        minWidth: 150,
        sortable: true,
        valueGetter: (value, row) => {
          const accountType = accountTypes.find(type => type.id === row.accountTypeId);
          return accountType?.displayName || accountType?.name || row.accountType;
        },
      },
      {
        field: 'currency',
        headerName: intl.get('accountManagement.currency'),
        flex: 0.7,
        minWidth: 90,
      },
      {
        field: 'balance',
        headerName: intl.get('accountManagement.balance'),
        flex: 1,
        minWidth: 120,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        sortComparator: (v1, v2, param1, param2) => {
          const balance1 = balanceMap.get(Number(param1.id)) || 0;
          const balance2 = balanceMap.get(Number(param2.id)) || 0;
          return balance1 - balance2;
        },
        renderCell: (params) => {
          const balance = balanceMap.get(params.row.id);
          if (balance === undefined) {
            return balancesLoading ? <CircularProgress size={16} /> : '-';
          }
          return balance.toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          });
        },
      },
      {
        field: 'corporateEntityName',
        headerName: intl.get('accountManagement.corporateEntity'),
        flex: 1.2,
        minWidth: 150,
        valueGetter: (value, row) => row.corporateEntityName || '-',
      },
      {
        field: 'isActive',
        headerName: intl.get('accountManagement.status'),
        flex: 0.8,
        minWidth: 100,
        renderCell: (params) => (
          <Chip
            label={params.value ? intl.get('accountManagement.active') : intl.get('accountManagement.inactive')}
            color={params.value ? 'success' : 'default'}
            size="small"
          />
        ),
      },
    ],
    [balanceMap, balancesLoading, accountTypes]
  );

  // Actions column with stable dependencies to prevent re-renders
  const actionsColumn: GridColDef = useMemo(
    () => ({
      field: 'actions',
      headerName: intl.get('common.actions'),
      flex: 2,
      minWidth: 240,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params) => (
        <Box 
          sx={{ 
            display: 'flex', 
            gap: 0.5, 
            flexWrap: 'wrap',
            pointerEvents: 'auto',
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <PermissionsGate viewScopes={[SCOPES['account.read']]} entityType={ENTITY_TYPE.CORPORATE_ENTITY}>
            <Tooltip title={intl.get('accountManagement.accountDetails')}>
              <IconButton
                size="small"
                data-action="details"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleDetails(params.row);
                }}
                color="info"
                sx={{ pointerEvents: 'auto' }}
              >
                <Icon icon="mdi:information" width={18} />
              </IconButton>
            </Tooltip>
          </PermissionsGate>
          <PermissionsGate viewScopes={[SCOPES['account.read']]} entityType={ENTITY_TYPE.CORPORATE_ENTITY}>
            <Tooltip title={intl.get('accountManagement.accountSummary')}>
              <IconButton
                size="small"
                data-action="summary"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleSummary(params.row);
                }}
                color="secondary"
                sx={{ pointerEvents: 'auto' }}
              >
                <Icon icon="mdi:chart-line" width={18} />
              </IconButton>
            </Tooltip>
          </PermissionsGate>
          <PermissionsGate viewScopes={[SCOPES['account.edit']]} entityType={ENTITY_TYPE.CORPORATE_ENTITY}>
            <Tooltip title={intl.get('common.edit')}>
              <IconButton
                size="small"
                data-action="edit"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleEdit(params.row);
                }}
                color="primary"
                sx={{ pointerEvents: 'auto' }}
              >
                <Icon icon="mdi:pencil" width={18} />
              </IconButton>
            </Tooltip>
          </PermissionsGate>
          <PermissionsGate viewScopes={[SCOPES['account.delete']]} entityType={ENTITY_TYPE.CORPORATE_ENTITY}>
            <Tooltip title={intl.get('common.delete')}>
              <IconButton
                size="small"
                data-action="delete"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleDelete(params.row.id, params.row.accountName);
                }}
                color="error"
                sx={{ pointerEvents: 'auto' }}
              >
                <Icon icon="mdi:delete" width={18} />
              </IconButton>
            </Tooltip>
          </PermissionsGate>
        </Box>
      ),
    }),
    [handleEdit, handleDetails, handleSummary, handleDelete]
  );

  // Combine base columns and actions column
  const columns: GridColDef[] = useMemo(() => [
    ...baseColumns,
    actionsColumn
  ], [baseColumns, actionsColumn]);

  const handleCellClick = useCallback((params: any, event: any) => {
    // Solo procesar clicks en la columna de actions
    if (params.field !== 'actions') return;
    
    // Buscar el botón clickeado basado en el elemento target
    const target = event.target.closest('button[data-action]');
    if (!target) return;
    
    const action = target.getAttribute('data-action');
    const account = params.row;
    
    // Prevenir que el DataGrid procese el click
    event.stopPropagation();
    event.preventDefault();
    
    // Ejecutar la acción correspondiente
    switch (action) {
      case 'details':
        handleDetails(account);
        break;
      case 'summary':
        handleSummary(account);
        break;
      case 'edit':
        handleEdit(account);
        break;
      case 'delete':
        handleDelete(account.id, account.accountName);
        break;
    }
  }, [handleDetails, handleSummary, handleEdit, handleDelete]);

  const renderCategoryAccordion = (categoryKey: string, accounts: Account[]) => {
    const summary = categorySummaries.get(categoryKey);
    const isExpanded = expandedCategories.has(categoryKey);
    
    const toggleCategory = () => {
      setExpandedCategories(prev => {
        const next = new Set(prev);
        if (isExpanded) {
          next.delete(categoryKey);
        } else {
          next.add(categoryKey);
        }
        return next;
      });
    };
    
    // Find category data for display name
    const categoryData = accountCategories.find(cat => cat.labelKey === categoryKey);
    const displayName = categoryData?.displayName || intl.get(categoryKey) || categoryKey;
    
    return (
      <Accordion
        key={categoryKey}
        expanded={isExpanded}
        onChange={toggleCategory}
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
            <Typography variant="h6">
              {displayName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Chip label={`${summary?.count || 0} ${intl.get('accountManagement.accounts')}`} size="small" />
              <Typography variant="body2" color="text.secondary">
                Total: ${summary?.balance.toLocaleString() || 0}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <DataGrid
            rows={accounts}
            columns={columns}
            disableRowSelectionOnClick
            onCellClick={handleCellClick}
            autoHeight
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            sortingMode="client"
            paginationMode="client"
            hideFooter
            disableColumnFilter
            disableColumnMenu
            sx={{
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
              },
              '& .MuiDataGrid-cell[data-field="actions"]': {
                cursor: 'default',
                '&:focus': {
                  outline: 'none',
                },
                '&:focus-within': {
                  outline: 'none',
                },
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '& .MuiBox-root': {
                  pointerEvents: 'auto',
                },
                '& .MuiIconButton-root': {
                  pointerEvents: 'auto',
                },
              },
              '& .MuiDataGrid-cell[data-field="actions"]:focus': {
                outline: 'none',
              },
            }}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {intl.get('accountManagement.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {intl.get('accountManagement.subtitle')}
            </Typography>
          </Box>
            <PermissionsGate viewScopes={[SCOPES['account.add']]} entityType={ENTITY_TYPE.CORPORATE_ENTITY}>
            <Button
              variant="contained"
              startIcon={<Icon icon="mdi:plus" />}
              onClick={handleCreate}
            >
              {intl.get('accountManagement.createAccount')}
            </Button>
          </PermissionsGate>
        </Box>

        {/* Filters */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            label={intl.get('searchBar.placeholder')}
            placeholder={intl.get('searchBar.placeholder')}
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: <Icon icon="mdi:magnify" width={20} style={{ marginRight: 8 }} />,
            }}
          />
          <TextField
            select
            label={intl.get('accountManagement.accountType')}
            value={filters.accountType || ''}
            onChange={(e) => handleFilterChange('accountType', e.target.value || undefined)}
            size="small"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{intl.get('common.allTypes')}</MenuItem>
            {accountTypes.map((accountType) => (
              <MenuItem key={accountType.id} value={accountType.id}>
                {accountType.displayName || accountType.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={intl.get('accountManagement.category')}
            value={filters.category || ''}
            onChange={(e) => handleFilterChange('category', e.target.value || undefined)}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">{intl.get('accountManagement.allCategories')}</MenuItem>
            {accountCategories.map((category) => (
              <MenuItem key={category.labelKey} value={category.labelKey}>
                {category.displayName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={intl.get('statusSelect.placeholder.status')}
            value={filters.isActive === undefined ? 'all' : filters.isActive ? 'active' : 'inactive'}
            onChange={(e) => {
              const val = e.target.value;
              handleFilterChange('isActive', val === 'all' ? undefined : val === 'active');
            }}
            size="small"
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="all">{intl.get('accountManagement.all')}</MenuItem>
            <MenuItem value="active">{intl.get('common.active')}</MenuItem>
            <MenuItem value="inactive">{intl.get('common.inactive')}</MenuItem>
          </TextField>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            startIcon={<Icon icon="mdi:filter-off" />}
          >
            {intl.get('accountManagement.clear')}
          </Button>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : groupedAccounts.size === 0 ? (
          <Alert severity="info">{intl.get('accountManagement.noAccountsFound')}</Alert>
        ) : (
          <Box>
            {Array.from(groupedAccounts.entries()).map(([categoryKey, accounts]) =>
              renderCategoryAccordion(categoryKey, accounts)
            )}
          </Box>
        )}
      </Card>

      {/* Form Drawer */}
      <AccountFormDrawer
        open={drawerOpen}
        account={selectedAccount}
        readOnly={readOnlyMode}
        onClose={() => setDrawerOpen(false)}
        onSuccess={handleDrawerSuccess}
      />
    </Box>
  );
};

export default AccountList;
